import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Sparkles, ShieldCheck, Clock, MapPin, Star, Wallet, Users, ArrowRight, Check } from "lucide-react";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="md:col-span-5 lg:col-span-7 animate-fade-up">
            <div className="label-eyebrow mb-6">India's Laundry Marketplace</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05]">
              Clean clothes,<br />
              <span className="text-[#1E3A8A]">clean connect.</span>
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-muted-foreground max-w-xl leading-relaxed">
              Book your nearest trusted local dhobi in seconds. We connect you with verified neighborhood laundries — premium service, fair prices, zero hassle.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/register?role=customer"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-[#1E3A8A] text-white font-bold tactile-btn"
                data-testid="hero-customer-cta"
              >
                Order Laundry <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/register?role=partner"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-white text-[#1E3A8A] font-bold border-2 border-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors"
                data-testid="hero-partner-cta"
              >
                Become a Partner
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-4 text-sm">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C2410C]" /> 200+ Verified Laundries</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C2410C]" /> 24-48hr Turnaround</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C2410C]" /> Pickup & Delivery</div>
            </div>
          </div>

          <div className="md:col-span-3 lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/c957144b-82e4-4b39-89b8-a7af382a453a/images/9c39dc4688d1af7f1bbdcedf23ee38ae62532bcb36e0d5bdac4f06b8363954e9.png"
                alt="Crisp folded laundry"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Now serving</div>
                    <div className="font-display font-bold text-xl">Bangalore</div>
                  </div>
                  <div className="flex items-center gap-1 text-[#C2410C]">
                    <Star className="w-5 h-5 fill-[#C2410C]" />
                    <span className="font-bold text-lg">4.7</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="label-eyebrow mb-4">How Dhobi Works</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-16 max-w-2xl">
            Three taps to fresh laundry.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: MapPin, num: "01", title: "Pick a laundry", desc: "Browse verified local partners near you. Compare ratings, services and prices." },
              { icon: Clock, num: "02", title: "Schedule pickup", desc: "Choose your service and a pickup time. We collect from your doorstep." },
              { icon: Sparkles, num: "03", title: "Get fresh laundry", desc: "Track every stage. Receive expertly cleaned clothes back in 24-48 hours." },
            ].map((step, i) => (
              <div key={i} className="bg-[#FAF8F5] rounded-2xl p-8 border border-border hover:shadow-lg transition-shadow">
                <div className="text-[#C2410C] text-sm font-black tracking-widest mb-4">{step.num}</div>
                <step.icon className="w-10 h-10 text-[#1E3A8A] mb-4" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-2xl mb-2">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER SECTION */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl order-2 lg:order-1">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/c957144b-82e4-4b39-89b8-a7af382a453a/images/f83d15a4d033b9bb56477081b0cc5705179da19ec840279162ed1ffe949cc575.png"
              alt="Proud laundry business owner"
              className="w-full h-[560px] object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <div className="label-eyebrow mb-4 text-[#C2410C]">For Laundry Owners</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-6">
              Grow your laundry business — no marketing budget required.
            </h2>
            <p className="text-lg text-muted-foreground mb-10 leading-relaxed">
              Join 200+ neighborhood dhobis already earning 30-50% more orders every month through Dhobi. We handle customers, payments, and delivery. You do what you do best — laundry.
            </p>

            <div className="space-y-5">
              {[
                { icon: Users, title: "More customers, daily", desc: "Get orders from your neighborhood automatically — no advertising needed." },
                { icon: Wallet, title: "Daily UPI payments", desc: "Get paid every day directly to your bank account. No long waits." },
                { icon: ShieldCheck, title: "Free training & branding kit", desc: "Branded bags, tags, uniforms and customer service training included." },
              ].map((b, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                    <b.icon className="w-6 h-6 text-[#1E3A8A]" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-bold text-lg">{b.title}</div>
                    <div className="text-muted-foreground">{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/register?role=partner"
              className="inline-flex items-center gap-2 mt-10 px-7 py-4 rounded-xl bg-[#C2410C] text-white font-bold tactile-btn-accent"
              data-testid="partner-signup-cta"
            >
              Partner with Dhobi <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1E3A8A] text-white relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-10" />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-6">
            Your clothes deserve a better dhobi.
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Join thousands of happy customers across Bangalore.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#C2410C] text-white font-bold tactile-btn-accent text-lg"
            data-testid="cta-signup"
          >
            Start Free — Order Now <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <footer className="bg-[#0F172A] text-white/60 py-12 text-center text-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="font-display font-black text-2xl text-white mb-2">Dhobi</div>
          <div>Clean Clothes, Clean Connect. © 2026 — Made in India.</div>
        </div>
      </footer>
    </div>
  );
}
